/* eslint no-param-reassign: "error" */
import {
  createSlice,
} from '@reduxjs/toolkit';

import type { MessageSnackBarType } from '@/components/MessageSnackbar';
import type {
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit';

import { logMaxMessageStringLength } from '@/constants/env';
import {
  checkApi403ForbiddenError,
  checkConditionalRequestFailedError,
  checkNetworkError,
  checkS3NetworkError,
  checkSessionError,
  eHandle,
} from '@/errors';
import { api } from '@/share';

/** ページタイプ */
export type PageType =
  'home'
  | 'child'
  | 'childApply'
  | 'childNotebook'
  | 'news'
  | 'notice'
  | 'attendContact'
  | 'attendHistory'
  | 'measure'
  | 'healthRecord'
  | 'openHomepage'
  | 'photo'
  | 'blog';

/** カテゴリ */
export type CategoryType = 'main' | 'sub' | 'homepage' | 'option'

/**
 * UIステート
 */
export type UiStateType = {
  /** アクションカウント */
  countAction: number,
  /** ブロッキング用アクションカウント */
  blockingAction: number,
  /** メッセージ */
  message?: MessageSnackBarType,
  /** ページ */
  page: PageType,
};

const initialState: UiStateType = {
  countAction: 0,
  blockingAction: 0,
  message: undefined,
  page: 'home',
};

/**
 * UI Slice
 */
export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    /** メッセージをセット */
    setMessage: (state, action: PayloadAction<MessageSnackBarType | undefined>) => {
      state.message = action.payload;
    },
    /** ブロッキングローディング開始 */
    startBlocking: (state) => {
      state.blockingAction += 1;
    },
    /** ブロッキングローディング終了 */
    endBlocking: (state) => {
      state.blockingAction = Math.max(0, state.blockingAction - 1);
    },
    /** ページをセット */
    setPage: (state, action: PayloadAction<PageType>) => {
      window.history.pushState({ page: action.payload }, '');
      state.page = action.payload;
    },
    /** ページを戻る */
    backPage: (state, action: PayloadAction<PageType>) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action: any) => {
        if (action.type && typeof action.type === 'string') {
          const actionType = action.type as string;
          let msg = action.payload ? JSON.stringify(action.payload) : '';
          if (msg.length > logMaxMessageStringLength) {
            msg = `${msg.substring(0, logMaxMessageStringLength)}...`;
          }
          api.logger.info(`Action start: ${actionType}: ${msg}`);
          return actionType.endsWith('/pending');
        }
        return false;
      },
      (state, _action) => {
        state.countAction += 1;
      },
    );
    builder.addMatcher(
      (action: any) => {
        if (action.type && typeof action.type === 'string') {
          const actionType = action.type as string;
          return actionType.endsWith('/fulfilled');
        }
        return false;
      },
      (state, action) => {
        api.logger.info(`Action end: ${action.type as string}`);
        state.countAction -= 1;
      },
    );
    builder.addMatcher(
      (action: any) => {
        if (action.type && typeof action.type === 'string') {
          const actionType = action.type as string;
          return actionType.endsWith('/rejected');
        }
        return false;
      },
      (state, action) => {
        state.countAction = 0;
        const { name, message }: { name: string, message: string } = action.error;
        if (checkSessionError(name, message)) {
          state.message = {
            key: 'error',
            content: 'セッションが切れました。再度ログインをしてください。',
          };
        } else if (checkS3NetworkError(name, message)) {
          state.message = {
            key: 'error',
            content: 'ファイルのアップロードに失敗しました。',
          };
        } else if (checkNetworkError(name, message)) {
          state.message = {
            key: 'error',
            content: 'ネットワークエラーが起きました。インターネット接続を確認してください。',
          };
        } else if (checkApi403ForbiddenError(name, message)) {
          state.message = {
            key: 'error',
            content: 'セッションが切れました。再度ログインをしてください。',
          };
        } else if (checkConditionalRequestFailedError(name, message)) {
          state.message = {
            key: 'error',
            content: '他の人によって更新または削除されたため保存できませんでした。',
          };
        } else {
          state.message = {
            key: 'error',
            content: '予期せぬエラーが起きました',
          };
          const type = action.type as string;
          const err = action.error as SerializedError;
          eHandle(state, type, err);
        }
      },
    );
  },
});
